import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import Card from "components/card";
import { FiRefreshCw, FiSearch } from "react-icons/fi";
import { MdArrowBack, MdClose, MdFilterList } from "react-icons/md";
import WarningGlyph from "components/WarningGlyph";
import AssetDetailsPopup from "./AssetDetailsPopup";
import { readToken, readUser } from "utils/session";
import { useAccountAccess, webFleetview } from "utils/access";
import { fetchFleetRows } from "utils/fleetRoster";
import { fetchOrgTree } from "utils/orgApi";
import { lastUpdateMillis, reportIsStale } from "utils/reportAge";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  getFilteredRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";

function batteryLevel(value) {
  const number = Number(String(value ?? "").replace("%", "").trim());
  if (value == null || value === "" || Number.isNaN(number)) return null;
  return Math.max(0, Math.min(100, number));
}

function batteryColor(level) {
  if (level < 5) return "#ff1a1a";
  if (level < 25) return "#f97316";
  if (level <= 50) return "#eab308";
  return "#22c55e";
}

function BatteryIcon({ level }) {
  const color = batteryColor(level);
  const fillWidth = (level / 100) * 18;
  return (
    <svg width="26" height="13" viewBox="0 0 28 14" aria-hidden="true" className="shrink-0">
      <rect x="0.8" y="1.8" width="22" height="10.4" rx="2.2" fill="none" stroke={color} strokeWidth="1.4" />
      <rect x="23.3" y="4.5" width="2.4" height="5" rx="0.8" fill={color} />
      {fillWidth > 0 && (
        <rect x="2.3" y="3.3" width={Math.max(fillWidth, 1.4)} height="7.4" rx="1" fill={color} />
      )}
    </svg>
  );
}

function signalBars(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return 0;
  const scale = number <= 31 ? 31 : 100;
  const ratio = Math.min(number, scale) / scale;
  if (ratio <= 0.25) return 1;
  if (ratio <= 0.5) return 2;
  if (ratio <= 0.75) return 3;
  return 4;
}

function SignalBars({ level }) {
  const heights = [4, 7, 10, 13];
  return (
    <svg width="18" height="14" viewBox="0 0 18 14" aria-hidden="true" className="shrink-0">
      {heights.map((height, index) => (
        <rect
          key={height}
          x={index * 4.5}
          y={14 - height}
          width="3"
          height={height}
          rx="0.6"
          fill={index < level ? "#16a34a" : "#d1d5db"}
        />
      ))}
    </svg>
  );
}

const WEEKDAY_LEAD = /^(Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sun|Mon|Tue|Wed|Thu|Fri|Sat)\b[,]?\s*/i;

function cleanStamp(value) {
  return String(value ?? "")
    .replace(/\s+GMT(?:\s*\([^)]*\))?(?=\s+\d{4}\s*$)/i, "")
    .replace(/\s+GMT(?:\s*\([^)]*\))?$/i, "")
    .trim();
}

function formatLastUpdate(value) {
  const text = cleanStamp(value);
  if (!text) return "-";
  const match = text.match(WEEKDAY_LEAD);
  if (!match) return text;
  const rest = text.slice(match[0].length).trim();
  return rest ? `${rest} ${match[1]}` : match[1];
}

const MOTION_ENUMS = ["MOVING", "WALKING", "HALTED", "NOT REPORTING"];
const WARNING_ENUMS = ["YES", "NO"];
const SETTLED_MOTION = new Set(["HALTED", "NOT REPORTING"]);
const CLEAR_WARNING = new Set(["NO"]);
export const BATTERY_OPTIONS = [
  { id: "critical", label: "Critical (< 5%)" },
  { id: "low", label: "Low (5-24%)" },
  { id: "fair", label: "Fair (25-50%)" },
  { id: "good", label: "Good (> 50%)" },
];

export function motionLabel(row) {
  if (reportIsStale(row.lastupdt)) return "NOT REPORTING";
  const raw = String(row.motstat ?? "").trim();
  return raw ? raw.toUpperCase() : "";
}

function warningLabel(value) {
  const raw = String(value ?? "").trim();
  if (!raw || raw.toLowerCase() === "no") return "NO";
  if (raw.toLowerCase() === "yes") return "YES";
  return raw.toUpperCase();
}

export function batteryBand(value) {
  const level = batteryLevel(value);
  if (level == null) return "";
  if (level < 5) return "critical";
  if (level < 25) return "low";
  if (level <= 50) return "fair";
  return "good";
}

function attentionRank(label, settled) {
  return settled.has(label) ? 0 : 1;
}

function enumColumnFilter(row, columnId, filterValue) {
  if (!Array.isArray(filterValue)) return true;
  if (columnId === "motstat") {
    const label = motionLabel(row.original);
    return label ? filterValue.includes(label) : false;
  }
  if (columnId === "warn") return filterValue.includes(warningLabel(row.getValue(columnId)));
  if (columnId === "batstat") {
    const band = batteryBand(row.getValue(columnId));
    return band ? filterValue.includes(band) : false;
  }
  return true;
}

function optionList(defaults, extras) {
  const seen = new Set();
  const ordered = [];
  [...defaults, ...extras].forEach((value) => {
    if (!value || seen.has(value)) return;
    seen.add(value);
    ordered.push(value);
  });
  return ordered;
}

function EnumFilterMenu({ title, options, selected, onToggle, onSelectAll, onClose, top, left }) {
  const menuRef = useRef(null);
  const allRef = useRef(null);
  const allOn = options.length > 0 && selected.length === options.length;
  const someOn = selected.length > 0 && !allOn;

  useEffect(() => {
    if (allRef.current) allRef.current.indeterminate = someOn;
  }, [someOn]);

  useEffect(() => {
    const onPointer = (event) => {
      if (menuRef.current && menuRef.current.contains(event.target)) return;
      onClose();
    };
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    const onScroll = (event) => {
      if (menuRef.current && event.target instanceof Node && menuRef.current.contains(event.target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={menuRef}
      style={{ top, left }}
      className="fixed z-[80] w-60 rounded-xl border border-gray-200 bg-white p-3 shadow-xl dark:border-white/10 dark:bg-navy-800"
    >
      <p className="mb-2 text-xs font-bold uppercase text-gray-500">{title}</p>
      <label className="mb-2 flex cursor-pointer items-center gap-2 border-b border-gray-100 pb-2 text-sm font-bold text-navy-700 dark:border-white/10 dark:text-white">
        <input ref={allRef} type="checkbox" checked={allOn} onChange={onSelectAll} className="h-4 w-4" />
        Select all
      </label>
      <div className="max-h-60 space-y-1 overflow-y-auto">
        {options.map((option) => (
          <label
            key={option.id}
            className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm text-navy-700 hover:bg-gray-50 dark:text-white dark:hover:bg-navy-700"
          >
            <input
              type="checkbox"
              checked={selected.includes(option.id)}
              onChange={() => onToggle(option.id)}
              className="h-4 w-4"
            />
            {option.label}
          </label>
        ))}
      </div>
    </div>,
    document.body
  );
}

function BranchLine({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase text-gray-500">{label}</p>
      <p className="text-sm text-navy-700 dark:text-white">{value || "—"}</p>
    </div>
  );
}

function placeAddress(place) {
  return [place?.address, place?.city, place?.state, place?.pincode, place?.country].filter(Boolean).join(", ");
}

function ContactList({ people }) {
  return (
    <div className="mt-4">
      <p className="text-xs font-semibold uppercase text-gray-500">Contacts</p>
      {people?.length ? (
        <ul className="mt-2 space-y-2">
          {people.map((person) => (
            <li key={person.username || person.displayName} className="text-sm text-navy-700 dark:text-white">
              {person.displayName || person.username}
              {person.phone ? ` · ${person.phone}` : ""}
              {person.email ? ` · ${person.email}` : ""}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-sm text-gray-500">No contact personnel</p>
      )}
    </div>
  );
}

function BranchDetailsPopup({ branch, onClose }) {
  const [showingZone, setShowingZone] = useState(false);
  useEffect(() => {
    setShowingZone(false);
  }, [branch]);
  if (!branch) return null;
  const zone = branch.zone;
  const place = showingZone && zone ? zone : branch;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl dark:bg-navy-800">
        <div className="mb-4 flex items-start justify-between gap-3 border-b border-gray-200 pb-3 dark:border-white/10">
          <div className="flex items-start gap-2">
            {showingZone && (
              <button
                type="button"
                onClick={() => setShowingZone(false)}
                aria-label="Back to branch"
                className="mt-1 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 dark:bg-navy-700"
              >
                <MdArrowBack className="h-5 w-5 text-gray-600 dark:text-white" />
              </button>
            )}
            <div>
              <h3 className="text-xl font-bold text-navy-700 dark:text-white">
                {place.name || (showingZone ? "Zone" : "Branch")}
              </h3>
              <p className="text-sm text-gray-500">
                {showingZone ? "Zone" : `Code ${branch.code || branch.branchId || "—"}`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 dark:bg-navy-700"
          >
            <MdClose className="h-5 w-5 text-gray-600 dark:text-white" />
          </button>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {!showingZone && (
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500">Zone</p>
              {zone ? (
                <button
                  type="button"
                  onClick={() => setShowingZone(true)}
                  className="text-sm font-bold text-blue-500 underline hover:text-blue-700"
                >
                  {zone.name || branch.zoneName || "Zone"}
                </button>
              ) : (
                <p className="text-sm text-navy-700 dark:text-white">{branch.zoneName || "—"}</p>
              )}
            </div>
          )}
          <BranchLine label="Phone" value={place.phone} />
          <BranchLine label="Email" value={place.email} />
          <BranchLine label="GST" value={place.gst} />
          <div className="sm:col-span-2">
            <BranchLine label="Address" value={placeAddress(place)} />
          </div>
          <div className="sm:col-span-2">
            <BranchLine label="Description" value={place.description} />
          </div>
        </div>
        <ContactList people={place.contacts} />
      </div>
    </div>
  );
}

function accountKind() {
  const user = readUser();
  return String(user?.access?.accountType || user?.accountType || "").toUpperCase();
}

function branchesByDevice(tree) {
  const byDid = new Map();
  (tree?.zones || []).forEach((zone) => {
    const zoneDetail = {
      zoneId: zone.zoneId,
      name: zone.name || "",
      description: zone.description || "",
      address: zone.address || "",
      phone: zone.phone || "",
      email: zone.email || "",
      gst: zone.gst || "",
      city: zone.city || "",
      state: zone.state || "",
      pincode: zone.pincode || "",
      country: zone.country || "",
      contacts: zone.contacts || [],
    };
    (zone.branches || []).forEach((branch) => {
      const detail = {
        branchId: branch.branchId,
        code: branch.branchId,
        name: branch.name || "",
        zoneName: zone.name || "",
        zone: zoneDetail,
        description: branch.description || "",
        address: branch.address || "",
        phone: branch.phone || "",
        email: branch.email || "",
        gst: branch.gst || "",
        city: branch.city || "",
        state: branch.state || "",
        pincode: branch.pincode || "",
        country: branch.country || "",
        contacts: branch.contacts || [],
      };
      (branch.devices || []).forEach((device) => {
        if (device?.did != null) byDid.set(String(device.did), detail);
      });
    });
  });
  return byDid;
}

const FLEET_COLUMNS = [
  ["astid", "Asset ID"],
  ["did", "Device ID"],
  ["lastupdt", "Last Update"],
  ["batstat", "Battery Status"],
  ["gsmss", "Signal Strength"],
  ["motstat", "Motion Status"],
  ["branchName", "Branch"],
  ["postype", "Location Type"],
  ["imei", "IMEI"],
  ["warn", "Warning"],
  ["simno", "SIM Number"],
  ["dirn", "Direction/speed"],
];

function rowsWithBranches(rows, byDid) {
  return rows.map((row) => {
    if (row.branch || row.branchName) return row;
    const branch = byDid.get(String(row.did));
    if (!branch) return row;
    return {
      ...row,
      zoneName: branch.zoneName,
      branchName: branch.name,
      branchCode: branch.code,
      branch,
    };
  });
}

function FastCacheBar({ cache, database }) {
  const cached = Number(cache) || 0;
  const fromDb = Number(database) || 0;
  const total = cached + fromDb;
  if (!total) return null;
  const cachePct = Math.round((cached / total) * 100);
  const databasePct = 100 - cachePct;
  return (
    <div
      className="flex items-center gap-2"
      title={`Fast Cache ${cachePct}%. Database ${databasePct}%.`}
    >
      <span className="text-xs font-medium text-gray-400">Fast Cache {cachePct}%</span>
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-amber-400/80">
        <div className="h-full bg-brand-500" style={{ width: `${cachePct}%` }} />
      </div>
    </div>
  );
}

function FleetViewTable() {
  const [sorting, setSorting] = useState([
    { id: "warn", desc: true },
    { id: "motstat", desc: true },
  ]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [fleetData, setFleetData] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const USERKEY = readToken();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [liveCache, setLiveCache] = useState(null);
  const [openFilter, setOpenFilter] = useState(null);
  const refreshInFlight = useRef(false);
  const fleetview = webFleetview(useAccountAccess());

  const motionOptions = useMemo(() => {
    const extras = fleetData.map((row) => motionLabel(row));
    return optionList(MOTION_ENUMS, extras).map((id) => ({ id, label: id }));
  }, [fleetData]);

  const warningOptions = useMemo(() => {
    const extras = fleetData.map((row) => warningLabel(row.warn));
    return optionList(WARNING_ENUMS, extras).map((id) => ({
      id,
      label: id === "YES" ? "Yes" : id === "NO" ? "No" : id,
    }));
  }, [fleetData]);

  const filterSpecs = {
    motstat: { title: "Motion Status", options: motionOptions },
    warn: { title: "Warning", options: warningOptions },
    batstat: { title: "Battery Status", options: BATTERY_OPTIONS },
  };

  const selectedIds = (columnId, options) => {
    const ids = options.map((option) => option.id);
    const entry = columnFilters.find((filter) => filter.id === columnId);
    if (!entry) return ids;
    return entry.value.filter((value) => ids.includes(value));
  };

  const writeEnumFilter = (columnId, next, options) => {
    const ids = options.map((option) => option.id);
    setColumnFilters((current) => {
      const rest = current.filter((filter) => filter.id !== columnId);
      if (next.length === ids.length) return rest;
      return [...rest, { id: columnId, value: next }];
    });
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  };

  const closeFilter = useCallback(() => setOpenFilter(null), []);

  const loadFleet = useCallback((forceRoster = false) => {
    const refreshList = forceRoster === true;
    if (refreshInFlight.current) return Promise.resolve();
    refreshInFlight.current = true;
    setRefreshing(true);
    return fetchFleetRows({ apiBaseUrl, userKey: USERKEY, forceRoster: refreshList })
      .then((rows) => {
        setLiveCache(rows.liveCache || null);
        const kind = accountKind();
        const needsOrg =
          (kind === "MASTER" || kind === "ADMIN" || kind === "ZONE") &&
          rows.some((row) => !row.branch);
        if (!needsOrg) {
          setFleetData(rows);
          return;
        }
        return fetchOrgTree()
          .then((tree) => {
            setFleetData(rowsWithBranches(rows, branchesByDevice(tree)));
          })
          .catch(() => {
            setFleetData(rows);
          });
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        refreshInFlight.current = false;
        setRefreshing(false);
      });
  }, [USERKEY, apiBaseUrl]);

  useEffect(() => {
    loadFleet();
  }, [loadFleet]);

  useEffect(() => {
    const onTimer = fleetview.trigger === "timer" || fleetview.trigger === "timer_and_notification";
    const onNotification = fleetview.trigger === "notification" || fleetview.trigger === "timer_and_notification";
    let timerId;
    if (onTimer) {
      timerId = window.setInterval(() => {
        loadFleet();
      }, fleetview.rateSeconds * 1000);
    }
    const refreshFromNotification = () => loadFleet();
    const refreshFromWorker = (event) => {
      if (event.data?.type === "new-fcm-notification") refreshFromNotification();
    };
    if (onNotification) {
      window.addEventListener("new-fcm-notification", refreshFromNotification);
      navigator.serviceWorker?.addEventListener("message", refreshFromWorker);
    }
    return () => {
      if (timerId) window.clearInterval(timerId);
      if (onNotification) {
        window.removeEventListener("new-fcm-notification", refreshFromNotification);
        navigator.serviceWorker?.removeEventListener("message", refreshFromWorker);
      }
    };
  }, [loadFleet, fleetview.trigger, fleetview.rateSeconds]);

  const columns = React.useMemo(() => {
    const columnHelper = createColumnHelper();
    const kind = accountKind();
    const showBranch = kind === "MASTER" || kind === "ADMIN" || kind === "ZONE";
    const hasKey = (key) => fleetData.some((row) => Object.prototype.hasOwnProperty.call(row, key));
    const ordered = FLEET_COLUMNS.filter(([key]) => {
      if (key === "branchName") return showBranch;
      if (key === "simno") return hasKey(key);
      return true;
    });
    return ordered.map(([key, label]) =>
      columnHelper.accessor(key, {
        sortDescFirst: key === "warn" || key === "motstat",
        ...(key === "motstat" || key === "warn" || key === "batstat" ? { filterFn: enumColumnFilter } : {}),
        header: ({ column }) => {
          const sorted = column.getIsSorted();
          return (
            <span className="inline-flex items-center gap-1 text-sm font-bold text-gray-600 dark:text-white">
              {label}
              <span className="inline-flex flex-col text-[9px] leading-none">
                <span className={sorted === "asc" ? "text-brand-500" : "text-gray-300"}>▲</span>
                <span className={sorted === "desc" ? "text-brand-500" : "text-gray-300"}>▼</span>
              </span>
            </span>
          );
        },
        sortingFn: (rowA, rowB, columnId) => {
          const left = rowA.getValue(columnId);
          const right = rowB.getValue(columnId);
          if (columnId === "warn") {
            return attentionRank(warningLabel(left), CLEAR_WARNING) - attentionRank(warningLabel(right), CLEAR_WARNING);
          }
          if (columnId === "motstat") {
            return attentionRank(motionLabel(rowA.original), SETTLED_MOTION) - attentionRank(motionLabel(rowB.original), SETTLED_MOTION);
          }
          if (columnId === "lastupdt") {
            const leftTime = lastUpdateMillis(left);
            const rightTime = lastUpdateMillis(right);
            if (leftTime == null && rightTime == null) return 0;
            if (leftTime == null) return 1;
            if (rightTime == null) return -1;
            return leftTime - rightTime;
          }
          const leftNumber = Number(left);
          const rightNumber = Number(right);
          const leftIsNumber = left !== null && left !== "" && !Number.isNaN(leftNumber);
          const rightIsNumber = right !== null && right !== "" && !Number.isNaN(rightNumber);
          if (leftIsNumber && rightIsNumber) return leftNumber - rightNumber;
          return String(left ?? "").localeCompare(String(right ?? ""), undefined, {
            numeric: true,
            sensitivity: "base",
          });
        },
        cell: (info) => {
          if (key === "astid") {
            return (
              <button
                onClick={(e) => {
                  e.preventDefault(); 
                  // Set the ID to trigger the AssetDetailsPopup to open
                  setSelectedAsset(info.row.original); 
                }}
                className="text-sm font-bold text-blue-500 hover:text-blue-700 underline cursor-pointer"
              >
                {info.getValue()}
              </button>
            );
          }

          if (key === "branchName") {
            const branch = info.row.original.branch;
            const label = info.getValue();
            if (!label) {
              return <span className="text-sm font-bold text-navy-700 dark:text-white">—</span>;
            }
            if (!branch) {
              return <span className="text-sm font-bold text-navy-700 dark:text-white">{label}</span>;
            }
            return (
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  setSelectedBranch(branch);
                }}
                className="text-sm font-bold text-blue-500 underline hover:text-blue-700"
              >
                {label}
              </button>
            );
          }

          if (key === "gsmss") {
            const raw = info.getValue();
            const missing = raw == null || raw === "";
            return (
              <span className="inline-flex items-center justify-center gap-2 text-sm font-bold text-navy-700 dark:text-white">
                <SignalBars level={missing ? 0 : signalBars(raw)} />
                {missing ? "-" : raw}
              </span>
            );
          }

          if (key === "motstat") {
            const raw = String(info.getValue() ?? "").trim();
            if (reportIsStale(info.row.original.lastupdt)) {
              return (
                <span className="text-[10px] font-medium leading-tight text-black dark:text-gray-300">
                  NOT REPORTING
                </span>
              );
            }
            const status = raw.toUpperCase();
            const color =
              status === "MOVING"
                ? "text-green-500"
                : status === "WALKING"
                  ? "text-yellow-500"
                  : status === "HALTED"
                    ? "text-red-500"
                    : "text-navy-700 dark:text-white";
            return <span className={`text-sm font-bold ${color}`}>{raw || "-"}</span>;
          }

          if (key === "warn") {
            const warning = String(info.getValue() ?? "").trim().toLowerCase() === "yes";
            if (!warning) return null;
            return (
              <WarningGlyph
                motion={info.row.original.motstat}
                stale={reportIsStale(info.row.original.lastupdt)}
                className="mx-auto h-5 w-5"
              />
            );
          }

          if (key === "batstat") {
            const raw = info.getValue();
            const level = batteryLevel(raw);
            return (
              <span className="inline-flex items-center gap-2 text-sm font-bold text-navy-700 dark:text-white">
                {level != null && <BatteryIcon level={level} />}
                {level == null ? "-" : `${String(raw).replace(/%\s*$/, "")}%`}
              </span>
            );
          }

          if (key === "dirn") {
            const direction = info.getValue();
            const speed = info.row.original.speed;
            const directionText = direction != null && direction !== "" ? `${direction}°` : "-";
            const speedText = speed != null && speed !== "" ? String(speed) : "-";
            const combined = directionText === "-" && speedText === "-" ? "-" : `${directionText} / ${speedText}`;
            return (
              <p className="whitespace-nowrap text-sm font-bold text-navy-700 dark:text-white">{combined}</p>
            );
          }

          // 2. Default render for all other columns
          const value = info.getValue();
          const shown =
            value !== null && value !== undefined && value !== ""
              ? key === "lastupdt"
                ? formatLastUpdate(value)
                : value
              : "-";
          return (
            <p className="text-sm font-bold text-navy-700 dark:text-white">
              {shown}
            </p>
          );
        },
      })
    );
  }, [fleetData]);

  const table = useReactTable({
    data: fleetData, // use the fleet data as table data
    columns,
    state: {
      sorting,
      pagination,
      columnFilters,
    },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    enableSortingRemoval: false,
  });

  return (
    <Card extra={"w-full h-full p-6"}>
      <header className="relative flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="text-xl font-bold text-navy-700 dark:text-white">
            Fleet View
          </div>
          <button
            type="button"
            onClick={() => loadFleet(true)}
            disabled={refreshing}
            className="flex items-center gap-1 rounded-full bg-lightPrimary px-3 py-2 text-sm font-medium text-navy-700 disabled:opacity-60 dark:bg-navy-900 dark:text-white"
          >
            <FiRefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <FastCacheBar cache={liveCache?.cache} database={liveCache?.database} />
          <label className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-white">
            Rows
            <select
              className="rounded-full bg-lightPrimary px-3 py-2 text-sm font-medium text-navy-700 outline-none dark:bg-navy-900 dark:text-white"
              value={table.getState().pagination.pageSize}
              onChange={(event) => table.setPageSize(Number(event.target.value))}
            >
              {[5, 10, 25].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="mb-6 flex items-center justify-center">
          <div className="mr-3 flex h-full items-center rounded-full bg-lightPrimary py-2 text-navy-700 dark:bg-navy-900 dark:text-white">
            <p className="pl-3 pr-2 text-xl">
              <FiSearch className="h-4 w-4 text-gray-400 dark:text-white" />
            </p>
            <input
              type="text"
              placeholder="Search by Asset ID..."
              className="block h-full w-full rounded-full bg-lightPrimary text-sm font-medium text-navy-700 outline-none placeholder:!text-gray-400 dark:bg-navy-900 dark:text-white dark:placeholder:!text-white sm:w-fit"
              onChange={(e) => {
                const value = e.target.value;
                setColumnFilters((current) => {
                  const rest = current.filter((filter) => filter.id !== "astid");
                  if (!value) return rest;
                  return [...rest, { id: "astid", value }];
                });
                setPagination((current) => ({ ...current, pageIndex: 0 }));
              }}
            />
          </div>
        </div>
      </header>

      {/* Adjust table container to enable horizontal scrolling based on column data */}
      <div
        className={`mt-8 ${
          columns.length > 5 ? "overflow-x-auto" : "overflow-x-hidden"
        }`}
      >
        <table className="w-full min-w-max">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="!border-px !border-gray-400">
                {headerGroup.headers.map((header) => {
                  const centered = header.column.id !== "astid";
                  const spec = filterSpecs[header.column.id];
                  const filterActive = columnFilters.some((filter) => filter.id === header.column.id);
                  return (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className={`border-b-[1px] border-gray-200 pb-2 pt-4 ${
                      centered ? "px-2 text-center" : "pr-4 text-start"
                    }`}
                  >
                    <div className={`flex items-center gap-1 text-xs text-gray-200 ${centered ? "justify-center" : "justify-start"}`}>
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        className="inline-flex cursor-pointer items-center"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </button>
                      {spec && (
                        <button
                          type="button"
                          aria-label={`Filter ${spec.title}`}
                          aria-expanded={openFilter?.id === header.column.id}
                          onMouseDown={(event) => event.stopPropagation()}
                          onClick={(event) => {
                            const rect = event.currentTarget.getBoundingClientRect();
                            setOpenFilter((current) => {
                              if (current?.id === header.column.id) return null;
                              const width = 240;
                              const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
                              return { id: header.column.id, top: rect.bottom + 6, left };
                            });
                          }}
                          className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-navy-700"
                        >
                          <MdFilterList className={`h-4 w-4 ${filterActive ? "text-brand-500" : "text-gray-400"}`} />
                        </button>
                      )}
                    </div>
                  </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={`min-w-[150px] border-white/0 py-3 ${
                      cell.column.id === "astid" ? "pr-4 text-start" : "px-2 text-center"
                    }`}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="relative mb-4 flex items-center justify-between pt-4 text-gray-600">
          <div>
            Showing {table.getRowModel().rows.length.toLocaleString()} of{" "}
            {table.getRowCount().toLocaleString()} Rows
          </div>
          <div className="flex items-center gap-2">
            <button
              className="rounded border p-1"
              onClick={() => table.firstPage()}
              disabled={!table.getCanPreviousPage()}
            >
              {"<<"}
            </button>
            <button
              className="rounded border p-1"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              {"<"}
            </button>
            <span className="flex items-center gap-1">
              <div>Page</div>
              <strong>
                {table.getState().pagination.pageIndex + 1} of{" "}
                {table.getPageCount().toLocaleString()}
              </strong>
            </span>
            <button
              className="rounded border p-1"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              {">"}
            </button>
            <button
              className="rounded border p-1"
              onClick={() => table.lastPage()}
              disabled={!table.getCanNextPage()}
            >
              {">>"}
            </button>
          </div>
        </div>
      </div>

      {/* 🚨 Add the popup component here */}
      <BranchDetailsPopup branch={selectedBranch} onClose={() => setSelectedBranch(null)} />
      <AssetDetailsPopup
        isOpen={!!selectedAsset}
        deviceId={selectedAsset?.did}
        fleetRow={selectedAsset}
        onClose={() => setSelectedAsset(null)}
      />
      {openFilter && filterSpecs[openFilter.id] && (
        <EnumFilterMenu
          title={filterSpecs[openFilter.id].title}
          options={filterSpecs[openFilter.id].options}
          selected={selectedIds(openFilter.id, filterSpecs[openFilter.id].options)}
          onToggle={(value) => {
            const options = filterSpecs[openFilter.id].options;
            const current = selectedIds(openFilter.id, options);
            const next = current.includes(value)
              ? current.filter((item) => item !== value)
              : [...current, value];
            writeEnumFilter(openFilter.id, next, options);
          }}
          onSelectAll={() => {
            const options = filterSpecs[openFilter.id].options;
            const ids = options.map((option) => option.id);
            const current = selectedIds(openFilter.id, options);
            writeEnumFilter(openFilter.id, current.length === ids.length ? [] : ids, options);
          }}
          onClose={closeFilter}
          top={openFilter.top}
          left={openFilter.left}
        />
      )}
    </Card>
  );
}

export default FleetViewTable;
